import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Search,
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  FileSpreadsheet,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return '—';
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const formatCurrency = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0,00 €';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(num);
};

const getDefaultDesde = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
};

const getDefaultHasta = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const RemesasVencimientos = () => {
  const { token, API_URL } = useAuth();

  const [vencimientos, setVencimientos] = useState([]);
  const [totalImporte, setTotalImporte] = useState(0);
  const [loading, setLoading] = useState(false);

  const [desde, setDesde] = useState(getDefaultDesde);
  const [hasta, setHasta] = useState(getDefaultHasta);
  const [buscarFactura, setBuscarFactura] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const [orderBy, setOrderBy] = useState('vencimiento_real');
  const [orderDir, setOrderDir] = useState('ASC');

  const [selectedFacturas, setSelectedFacturas] = useState(new Set());
  const [actionLoading, setActionLoading] = useState(null);
  const [estadoTab, setEstadoTab] = useState('pendientes'); // 'pendientes' | 'enviadas'
  const [showDesmarcarModal, setShowDesmarcarModal] = useState(false);
  const [nuevaFecha, setNuevaFecha] = useState(getDefaultHasta);

  const fetchVencimientos = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        desde,
        hasta,
        order_by: orderBy,
        order_dir: orderDir,
        estado: estadoTab,
      });
      if (activeSearch) params.set('buscar_factura', activeSearch);

      const res = await fetch(`${API_URL}/contabilidad/remesas-vencimientos?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar remesas');
      const data = await res.json();
      setVencimientos(data.vencimientos || []);
      setTotalImporte(data.totalImporte || 0);
      setSelectedFacturas(new Set());
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, desde, hasta, orderBy, orderDir, activeSearch, estadoTab]);

  useEffect(() => {
    fetchVencimientos();
  }, [fetchVencimientos]);

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrderDir((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setOrderBy(column);
      setOrderDir('ASC');
    }
  };

  const getSortIcon = (column) => {
    if (orderBy !== column) return <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-40" />;
    return orderDir === 'ASC'
      ? <ArrowUp className="w-3.5 h-3.5 ml-1 text-blue-600" />
      : <ArrowDown className="w-3.5 h-3.5 ml-1 text-blue-600" />;
  };

  const handleSearch = () => setActiveSearch(buscarFactura.trim());
  const handleClearSearch = () => {
    setBuscarFactura('');
    setActiveSearch('');
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedFacturas(new Set(vencimientos.map((v) => v.factura)));
    } else {
      setSelectedFacturas(new Set());
    }
  };

  const handleSelectFactura = (factura) => {
    setSelectedFacturas((prev) => {
      const next = new Set(prev);
      if (next.has(factura)) next.delete(factura);
      else next.add(factura);
      return next;
    });
  };

  const handleMarcarPagadas = async () => {
    if (selectedFacturas.size === 0) {
      alert('Debe seleccionar al menos una factura.');
      return;
    }
    if (!window.confirm(`¿Está seguro de que desea marcar ${selectedFacturas.size} factura(s) como pagadas?`)) return;

    try {
      setActionLoading('marcar');
      const res = await fetch(`${API_URL}/contabilidad/remesas-vencimientos/marcar-pagadas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ facturas: Array.from(selectedFacturas) }),
      });
      if (!res.ok) throw new Error('Error al marcar como pagadas');
      alert('Facturas marcadas como pagadas correctamente.');
      await fetchVencimientos();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDesmarcarPagadas = () => {
    if (selectedFacturas.size === 0) {
      alert('Debe seleccionar al menos una factura.');
      return;
    }
    setNuevaFecha(getDefaultHasta());
    setShowDesmarcarModal(true);
  };

  const confirmDesmarcarPagadas = async () => {
    if (!nuevaFecha) {
      alert('Debe seleccionar una fecha.');
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (nuevaFecha < today) {
      alert('La nueva fecha debe ser hoy o posterior.');
      return;
    }

    try {
      setActionLoading('desmarcar');
      const res = await fetch(`${API_URL}/contabilidad/remesas-vencimientos/desmarcar-pagadas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          facturas: Array.from(selectedFacturas),
          nueva_fecha: nuevaFecha,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al desmarcar');
      alert(data.message || 'Facturas movidas a pendientes correctamente.');
      setShowDesmarcarModal(false);
      await fetchVencimientos();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleExcelDownload = async () => {
    try {
      setActionLoading('excel');
      const params = new URLSearchParams({ desde, hasta, tipo: 'remesas' });
      const res = await fetch(`${API_URL}/contabilidad/vencimientos/excel?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al descargar Excel');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facturas_remesas_${desde}_${hasta}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const allSelected = vencimientos.length > 0 && selectedFacturas.size === vencimientos.length;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Facturas por Recibo Bancario</h1>
        </div>

        {/* Tabs Pendientes / Enviadas */}
        <div className="flex border-b border-gray-200 mb-4">
          <button
            onClick={() => { setEstadoTab('pendientes'); setSelectedFacturas(new Set()); }}
            className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
              estadoTab === 'pendientes'
                ? 'border-[#0099FF] text-[#0099FF]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Pendientes
          </button>
          <button
            onClick={() => { setEstadoTab('enviadas'); setSelectedFacturas(new Set()); }}
            className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
              estadoTab === 'enviadas'
                ? 'border-[#0099FF] text-[#0099FF]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Enviadas
          </button>
          <button
            onClick={() => { setEstadoTab('eliminadas'); setSelectedFacturas(new Set()); }}
            className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
              estadoTab === 'eliminadas'
                ? 'border-[#0099FF] text-[#0099FF]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Eliminadas de remesa
          </button>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Desde</label>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Hasta</label>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={fetchVencimientos}
              disabled={loading}
              className="px-4 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: '#0099FF' }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Actualizar
            </button>
            <button
              onClick={handleExcelDownload}
              disabled={actionLoading !== null}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {actionLoading === 'excel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Descargar Excel
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por Nº Factura"
              value={buscarFactura}
              onChange={(e) => setBuscarFactura(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors flex items-center gap-2"
            style={{ backgroundColor: '#0099FF' }}
          >
            <Search className="w-4 h-4" />
            Buscar
          </button>
          {activeSearch && (
            <button
              onClick={handleClearSearch}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {!loading && vencimientos.length > 0 && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600">
              Remesas del <span className="font-medium">{formatDate(desde)}</span> al <span className="font-medium">{formatDate(hasta)}</span>
              {activeSearch && (
                <span className="ml-2 text-gray-400">— Filtrado por: {activeSearch}</span>
              )}
            </p>
            <p className="text-sm font-semibold text-gray-700">
              Total: {formatCurrency(totalImporte)}
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <span className="ml-3 text-gray-500">Cargando remesas...</span>
            </div>
          ) : vencimientos.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No se encontraron remesas en el rango seleccionado.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CIF</th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                      onClick={() => handleSort('cliente')}
                    >
                      <span className="flex items-center">
                        Razón Social
                        {getSortIcon('cliente')}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vto. Factura</th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-blue-600 select-none"
                      onClick={() => handleSort('vencimiento_real')}
                    >
                      <span className="flex items-center">
                        Vto. Real
                        {getSortIcon('vencimiento_real')}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días Pago</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nº Factura</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Forma Pago</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IBAN</th>
                    <th className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {vencimientos.map((v, idx) => (
                    <tr
                      key={`${v.factura}-${idx}`}
                      className="hover:bg-blue-50/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">{v.cif}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 truncate" title={v.razon_social}>
                        {v.razon_social}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(v.fecha_vencimiento_factura)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-blue-700">{formatDate(v.vencimiento_real)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {v.dias_pago ? v.dias_pago : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-mono">{v.factura}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(v.total)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 truncate" title={v.forma_pago_desc}>
                        {v.forma_pago_desc || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono truncate" title={v.iban}>
                        {v.iban || '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedFacturas.has(v.factura)}
                          onChange={() => handleSelectFactura(v.factura)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 border-t-2 border-gray-300">
                    <td colSpan={6} className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">
                      Total Importe:
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-blue-700 text-sm">
                      {formatCurrency(totalImporte)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {!loading && vencimientos.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            {estadoTab === 'pendientes' ? (
              <button
                onClick={handleMarcarPagadas}
                disabled={actionLoading !== null}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading === 'marcar' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Marcar como Pagadas las Facturas Seleccionadas
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleDesmarcarPagadas}
                disabled={actionLoading !== null}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {actionLoading === 'desmarcar' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4" />
                    Mover de vuelta a Pendientes
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal: Nueva fecha de vencimiento al desmarcar */}
      {showDesmarcarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Nueva fecha de vencimiento</h3>
            <p className="text-sm text-gray-600 mb-4">
              Vas a mover <strong>{selectedFacturas.size}</strong> factura(s) de vuelta a pendientes.
              Indica la nueva fecha en la que deberá cobrarse.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nueva fecha de cobro</label>
              <input
                type="date"
                value={nuevaFecha}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setNuevaFecha(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Debe ser hoy o una fecha posterior.</p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDesmarcarModal(false)}
                disabled={actionLoading === 'desmarcar'}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDesmarcarPagadas}
                disabled={actionLoading === 'desmarcar'}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 flex items-center gap-2 disabled:opacity-50"
              >
                {actionLoading === 'desmarcar' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemesasVencimientos;
